<template>
  <div v-if="show" class="app-modal" role="presentation">
    <div class="app-modal__backdrop" @click="emitClose"></div>

    <section
      class="app-modal__dialog customer-export-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="customer-export-modal-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="customer-export-modal-title">{{ text.exports.modal.title }}</h3>
          <p>{{ text.exports.modal.subtitle }}</p>
        </div>
        <button type="button" class="app-modal__close" @click="emitClose">
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="app-modal__content customer-export-modal__content">
        <div class="customer-export-modal__form-body">
          <section class="app-modal__section">
            <h4>{{ text.exports.modal.format.title }}</h4>

            <div class="customer-export-modal__format-row">
              <div class="customer-export-modal__field">
                <label>{{ text.exports.modal.format.selectorLabel }}</label>
                <multiselect
                  v-model="selectedFormatOption"
                  :options="formatOptions"
                  track-by="value"
                  label="label"
                  :show-labels="false"
                  :searchable="true"
                  :allow-empty="true"
                  :max-height="240"
                  :placeholder="text.exports.modal.format.selectorPlaceholder"
                  open-direction="below"
                  class="app-modal-select"
                  :disabled="loadingInitial || savingFormat || deletingFormat || isFormatSelectorDisabled"
                >
                  <template #noResult>
                    <span>{{ text?.common?.noOptionAvailable || 'Sin opciones' }}</span>
                  </template>
                  <template #noOptions>
                    <span>{{ text?.common?.noOptionAvailable || 'Sin opciones' }}</span>
                  </template>
                </multiselect>
              </div>

              <div class="customer-export-modal__field customer-export-modal__field--name-inline">
                <label>{{ text.exports.modal.format.nameLabel }}</label>
                <input
                  v-model="formatName"
                  type="text"
                  class="form-control"
                  :placeholder="text.exports.modal.format.namePlaceholder"
                  maxlength="120"
                >
                <small v-if="formatNameError" class="customer-export-modal__field-error">{{ formatNameError }}</small>
              </div>
            </div>
          </section>

          <section class="app-modal__section">
            <h4>{{ text.exports.modal.columns.title }}</h4>
            <div class="customer-export-modal__columns-toolbar">
              <p class="customer-export-modal__columns-subtitle">{{ text.exports.modal.columns.subtitle }}</p>
              <div class="customer-export-modal__actions-panel">
                <div class="customer-export-modal__quick-actions">
                  <button type="button" class="customer-export-modal__quick-action-btn" @click="selectAllColumns">
                    <i class="fa fa-check-square-o" aria-hidden="true"></i>
                    <span>{{ text.exports.modal.columns.selectAll }}</span>
                  </button>
                  <button type="button" class="customer-export-modal__quick-action-btn" @click="clearColumns">
                    <i class="fa fa-eraser" aria-hidden="true"></i>
                    <span>{{ text.exports.modal.columns.clearSelection }}</span>
                  </button>
                  <button type="button" class="customer-export-modal__quick-action-btn" @click="restoreDefaultColumns">
                    <i class="fa fa-refresh" aria-hidden="true"></i>
                    <span>{{ text.exports.modal.columns.restoreDefault }}</span>
                  </button>
                </div>
                <p class="customer-export-modal__selected-count">{{ selectedCountLabel }}</p>
              </div>
            </div>

            <div
              v-for="group in groupedColumns"
              :key="group.key"
              class="customer-export-modal__group"
            >
              <h5>{{ resolveGroupLabel(group.key) }}</h5>
              <div class="customer-export-modal__columns-grid">
                <article
                  v-for="column in group.columns"
                  :key="column.key"
                  class="customer-export-modal__column-card"
                >
                  <div>
                    <strong>{{ column.label }}</strong>
                    <small v-if="column.description">{{ column.description }}</small>
                  </div>
                  <label class="customer-export-modal__switch">
                    <input
                      type="checkbox"
                      :checked="isColumnSelected(column.key)"
                      @change="toggleColumn(column.key, $event.target.checked)"
                    >
                    <span></span>
                  </label>
                </article>
              </div>
              <div v-if="resolveGroupWarning(group.key)" class="customer-export-modal__group-warning" role="note">
                <i class="fa fa-info-circle" aria-hidden="true"></i>
                <span>{{ resolveGroupWarning(group.key) }}</span>
              </div>
            </div>
          </section>

          <section class="app-modal__section">
            <h4>{{ text.exports.modal.columns.orderTitle }}</h4>
            <p class="customer-export-modal__section-subtitle">{{ text.exports.modal.columns.orderSubtitle }}</p>

            <div v-if="orderedSelectedColumns.length > 0" class="customer-export-modal__order-list">
              <div
                v-for="(column, index) in orderedSelectedColumns"
                :key="column.key"
                class="customer-export-modal__order-item"
              >
                <span>{{ index + 1 }}. {{ column.label }}</span>
                <div class="customer-export-modal__order-actions">
                  <button
                    type="button"
                    class="app-table-icon-btn"
                    :disabled="index === 0"
                    @click="moveSelectedColumn(index, -1)"
                  >
                    <i class="fa fa-arrow-up" aria-hidden="true"></i>
                  </button>
                  <button
                    type="button"
                    class="app-table-icon-btn"
                    :disabled="index >= orderedSelectedColumns.length - 1"
                    @click="moveSelectedColumn(index, 1)"
                  >
                    <i class="fa fa-arrow-down" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </div>
            <p v-else class="customer-export-modal__empty-note">{{ text.exports.modal.columns.orderEmpty }}</p>
          </section>

          <section v-if="supportsPdfExport || showIncludeSummary || showIncludeLogo" class="app-modal__section">
            <h4>{{ text?.exports?.modal?.exportOptions?.title || 'Opciones de exportacion' }}</h4>

            <div v-if="supportsPdfExport" class="customer-export-modal__field">
              <label>{{ text?.exports?.modal?.exportOptions?.outputLabel || 'Formato de salida' }}</label>
              <div class="customer-export-modal__option-checkboxes">
                <label class="app-modal-toggle" :class="{ 'app-modal-toggle--disabled': isBusy }">
                  <input
                    type="checkbox"
                    :checked="exportOutput === 'excel'"
                    :disabled="isBusy"
                    @change="setExportOutput('excel', $event.target.checked)"
                  />
                  <span>{{ text?.exports?.modal?.exportOptions?.outputOptions?.excel || 'Excel' }}</span>
                </label>

                <label class="app-modal-toggle" :class="{ 'app-modal-toggle--disabled': isBusy }">
                  <input
                    type="checkbox"
                    :checked="exportOutput === 'pdf'"
                    :disabled="isBusy"
                    @change="setExportOutput('pdf', $event.target.checked)"
                  />
                  <span>{{ text?.exports?.modal?.exportOptions?.outputOptions?.pdf || 'PDF' }}</span>
                </label>
              </div>
            </div>

            <label v-if="showIncludeSummary" class="app-modal-toggle customer-export-modal__summary-toggle" :class="{ 'app-modal-toggle--disabled': isBusy }">
              <input v-model="includeSummary" type="checkbox" :disabled="isBusy">
              <span>{{ text?.exports?.modal?.exportOptions?.includeSummaryLabel || 'Incluir resumen de la cuenta' }}</span>
            </label>

            <label
              v-if="showIncludeLogo && supportsPdfExport && exportOutput === 'pdf'"
              class="app-modal-toggle customer-export-modal__summary-toggle"
              :class="{ 'app-modal-toggle--disabled': isBusy }"
            >
              <input v-model="includeLogo" type="checkbox" :disabled="isBusy">
              <span>{{ text?.exports?.modal?.exportOptions?.includeLogoLabel || 'Mostrar logo de la cuenta (PDF)' }}</span>
            </label>
          </section>
        </div>

        <footer class="app-modal__footer customer-export-modal__footer">
          <div class="app-modal__footer-left">
            <button
              v-if="canDeleteFormat"
              type="button"
              class="app-table-icon-btn app-table-icon-btn--delete customer-export-modal__delete-btn"
              :title="text.exports.settings.table.actions.delete"
              :disabled="isBusy"
              @click="openDeleteConfirm"
            >
              <i class="fa fa-trash" aria-hidden="true"></i>
            </button>
          </div>
          <div class="app-modal__footer-right">
            <button type="button" class="btn btn-light" :disabled="isBusy" @click="emitClose">{{ text.exports.modal.footer.cancel }}</button>
            <button v-if="allowExport" type="button" class="btn btn-secondary" :disabled="isBusy" @click="handleSaveAndExport">{{ text.exports.modal.footer.saveAndExport }}</button>
            <button v-if="allowExport" type="button" class="btn btn-primary" :disabled="!canExport || isBusy" @click="handleExport">
              {{ exporting ? text.exports.modal.footer.exporting : text.exports.modal.footer.exportOnly }}
            </button>
          </div>
        </footer>
      </div>
    </section>

    <ConfirmActionModal
      :show="isDeleteConfirmOpen"
      :title="text.exports.settings.deleteModal.title"
      :subtitle="text.exports.settings.deleteModal.subtitle"
      :message="text.exports.settings.deleteModal.message(resolveSelectedFormatName())"
      :confirm-text="text.exports.settings.deleteModal.confirm"
      :cancel-text="text?.actions?.common?.cancel || 'Cancelar'"
      @confirm="handleDeleteFormat"
      @cancel="closeDeleteConfirm"
    />
  </div>
</template>

<script>
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import { extractApiErrorMessage, extractCollection, extractPayload } from '@/utils/dataNormalization';

export default {
  name: 'ExportFormatModal',
  components: {
    ConfirmActionModal,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    searchTerm: {
      type: String,
      default: '',
    },
    pageCustomerIds: {
      type: Array,
      default: () => [],
    },
    initialFormatId: {
      type: [Number, String],
      default: 0,
    },
    lockFormatSelector: {
      type: Boolean,
      default: false,
    },
    textCatalog: {
      type: Object,
      required: true,
    },
    exportService: {
      type: Object,
      required: true,
    },
    fileNamePrefix: {
      type: String,
      default: 'export',
    },
    showIncludeSummary: {
      type: Boolean,
      default: false,
    },
    showIncludeLogo: {
      type: Boolean,
      default: false,
    },
    extraPayload: {
      type: Object,
      default: () => ({}),
    },
    allowExport: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['close'],
  data() {
    return {
      loadingInitial: false,
      savingFormat: false,
      deletingFormat: false,
      exporting: false,
      columnsCatalog: [],
      formats: [],
      selectedFormatId: 'new',
      formatName: '',
      formatNameError: '',
      selectedColumns: [],
      isDeleteConfirmOpen: false,
      exportOutput: 'excel',
      includeSummary: false,
      includeLogo: false,
    };
  },
  computed: {
    text() {
      return this.textCatalog || {};
    },
    isBusy() {
      return this.loadingInitial || this.savingFormat || this.deletingFormat || this.exporting;
    },
    canExport() {
      return this.allowExport && this.selectedColumns.length > 0;
    },
    canDeleteFormat() {
      return this.selectedFormat !== null;
    },
    isFormatSelectorDisabled() {
      return this.lockFormatSelector;
    },
    supportsPdfExport() {
      return typeof this.exportService?.exportPdf === 'function';
    },
    formatOptions() {
      const base = [{ value: 'new', label: this.text.exports.modal.format.newOption }];
      const dynamic = this.formats.map((format) => ({
        value: String(format.id),
        label: `${format.name}`,
      }));

      return [...base, ...dynamic];
    },
    selectedFormatOption: {
      get() {
        return this.formatOptions.find((option) => option.value === String(this.selectedFormatId || 'new')) || this.formatOptions[0];
      },
      set(option) {
        this.selectedFormatId = String(option?.value || 'new');
        this.onFormatChange();
      },
    },
    selectedCountLabel() {
      return this.text.exports.modal.columns.selectedCount(this.selectedColumns.length);
    },
    selectedFormat() {
      const formatId = Number(this.selectedFormatId || 0);
      if (formatId <= 0) {
        return null;
      }

      return this.formats.find((format) => Number(format?.id || 0) === formatId) || null;
    },
    groupedColumns() {
      const groups = {};

      this.columnsCatalog.forEach((column) => {
        const key = String(column?.group || 'general');
        if (!groups[key]) {
          groups[key] = [];
        }

        groups[key].push(column);
      });

      return Object.keys(groups).map((key) => ({
        key,
        columns: groups[key],
      }));
    },
    orderedSelectedColumns() {
      return this.selectedColumns
        .map((key) => this.columnsCatalog.find((column) => column.key === key))
        .filter(Boolean);
    },
  },
  watch: {
    show(value) {
      if (value) {
        this.initialize();
      }
    },
  },
  methods: {
    getServiceMethod(name) {
      const method = this.exportService?.[name];
      if (typeof method !== 'function') {
        throw new Error(`missing_export_service_method:${name}`);
      }

      return method;
    },
    async initialize() {
      this.loadingInitial = true;
      this.formatNameError = '';
      this.exportOutput = 'excel';
      this.includeSummary = this.showIncludeSummary;
      this.includeLogo = this.showIncludeLogo;

      try {
        const exportColumns = this.getServiceMethod('exportColumns');
        const exportFormats = this.getServiceMethod('exportFormats');

        const [columnsResponse, formatsResponse] = await Promise.all([
          exportColumns(),
          exportFormats(),
        ]);

        const columnsPayload = extractPayload(columnsResponse) || {};
        const formatsPayload = extractCollection(formatsResponse);

        const columns = Array.isArray(columnsPayload?.columns) ? columnsPayload.columns : [];
        this.columnsCatalog = [...columns].sort((left, right) => Number(left?.order || 0) - Number(right?.order || 0));

        this.formats = Array.isArray(formatsPayload) ? formatsPayload : [];
        const initialFormatId = Number(this.initialFormatId || 0);
        const hasInitialFormat = initialFormatId > 0
          && this.formats.some((format) => Number(format?.id || 0) === initialFormatId);

        this.selectedFormatId = hasInitialFormat ? String(initialFormatId) : 'new';
        if (hasInitialFormat) {
          this.onFormatChange();
        } else {
          this.applyDefaultColumns();
          this.formatName = '';
        }
      } catch (error) {
        const message = error?.response?.data?.message || this.text.exports.modal.toast.loadError;
        this.$toast?.error(message);
      } finally {
        this.loadingInitial = false;
      }
    },
    emitClose() {
      if (this.isBusy) {
        return;
      }

      this.$emit('close');
    },
    resolveSelectedFormatName() {
      return String(this.selectedFormat?.name || '').trim() || '-';
    },
    openDeleteConfirm() {
      if (!this.canDeleteFormat || this.isBusy) {
        return;
      }

      this.isDeleteConfirmOpen = true;
    },
    closeDeleteConfirm() {
      this.isDeleteConfirmOpen = false;
    },
    resolveGroupLabel(groupKey) {
      return this.text.exports.modal.groups[groupKey] || groupKey;
    },
    resolveGroupWarning(groupKey) {
      const warningByGroup = this.text?.exports?.modal?.columns?.warningsByGroup || {};
      const mappedWarning = String(warningByGroup[groupKey] || '').trim();
      if (mappedWarning !== '') {
        return mappedWarning;
      }

      if (groupKey === 'contact') {
        return String(this.text?.exports?.modal?.columns?.contactFallbackWarning || '').trim();
      }

      if (groupKey === 'fiscal') {
        return String(this.text?.exports?.modal?.columns?.fiscalDefaultWarning || '').trim();
      }

      return '';
    },
    applyDefaultColumns() {
      const defaults = this.columnsCatalog
        .filter((column) => Boolean(column?.default))
        .map((column) => column.key);

      this.selectedColumns = defaults;
    },
    isColumnSelected(columnKey) {
      return this.selectedColumns.includes(columnKey);
    },
    toggleColumn(columnKey, shouldEnable) {
      const key = String(columnKey || '').trim();
      if (!key) {
        return;
      }

      if (shouldEnable) {
        if (!this.selectedColumns.includes(key)) {
          this.selectedColumns = [...this.selectedColumns, key];
        }
        return;
      }

      this.selectedColumns = this.selectedColumns.filter((item) => item !== key);
    },
    selectAllColumns() {
      this.selectedColumns = this.columnsCatalog.map((column) => column.key);
    },
    clearColumns() {
      this.selectedColumns = [];
    },
    restoreDefaultColumns() {
      this.applyDefaultColumns();
    },
    moveSelectedColumn(index, direction) {
      const currentIndex = Number(index);
      const nextIndex = currentIndex + Number(direction || 0);

      if (currentIndex < 0 || nextIndex < 0 || nextIndex >= this.selectedColumns.length) {
        return;
      }

      const next = [...this.selectedColumns];
      const temp = next[currentIndex];
      next[currentIndex] = next[nextIndex];
      next[nextIndex] = temp;
      this.selectedColumns = next;
    },
    setExportOutput(target, checked) {
      const normalizedTarget = String(target || '').trim().toLowerCase();
      if (!['excel', 'pdf'].includes(normalizedTarget)) {
        return;
      }

      if (!checked) {
        if (this.exportOutput === normalizedTarget) {
          this.exportOutput = normalizedTarget === 'excel' ? 'pdf' : 'excel';
        }
        return;
      }

      this.exportOutput = normalizedTarget;
    },
    onFormatChange() {
      this.formatNameError = '';

      if (String(this.selectedFormatId) === 'new') {
        this.formatName = '';
        this.applyDefaultColumns();
        return;
      }

      const format = this.selectedFormat;
      if (!format) {
        return;
      }

      const columns = Array.isArray(format?.columns) ? format.columns : [];
      this.selectedColumns = columns.filter((key) => this.columnsCatalog.some((column) => column.key === key));
      this.formatName = String(format?.name || '');

      if (this.selectedColumns.length <= 0) {
        this.applyDefaultColumns();
      }
    },
    validateFormatName() {
      if (String(this.formatName || '').trim() === '') {
        this.formatNameError = this.text.exports.modal.format.nameRequired;
        return false;
      }

      this.formatNameError = '';
      return true;
    },
    async handleSaveFormat() {
      if (this.selectedColumns.length <= 0) {
        this.$toast?.error(this.text.exports.modal.toast.noColumns);
        return false;
      }

      if (!this.validateFormatName()) {
        return false;
      }

      this.savingFormat = true;

      const payload = {
        name: String(this.formatName || '').trim(),
        scope_default: 'all',
        columns: this.selectedColumns,
      };

      try {
        if (this.selectedFormat) {
          const exportFormatUpdate = this.getServiceMethod('exportFormatUpdate');
          const response = await exportFormatUpdate(this.selectedFormat.id, payload);
          const saved = extractPayload(response) || null;
          await this.reloadFormats(saved?.id || this.selectedFormat.id);
          this.$toast?.success(this.text.exports.modal.toast.formatUpdated);
          return true;
        }

        const exportFormatStore = this.getServiceMethod('exportFormatStore');
        const response = await exportFormatStore(payload);
        const saved = extractPayload(response) || null;
        await this.reloadFormats(saved?.id || 0);
        this.$toast?.success(this.text.exports.modal.toast.formatSaved);
        return true;
      } catch (error) {
        const backendMessage = String(extractApiErrorMessage(error?.response) || error?.response?.data?.message || '');

        if (backendMessage.toLowerCase().includes('ya existe')) {
          const replace = window.confirm(this.text.exports.modal.format.duplicateConfirm);
          if (replace) {
            try {
              const exportFormatStore = this.getServiceMethod('exportFormatStore');
              const retryResponse = await exportFormatStore({
                ...payload,
                replace: 1,
              });
              const saved = extractPayload(retryResponse) || null;
              await this.reloadFormats(saved?.id || 0);
              this.$toast?.success(this.text.exports.modal.toast.formatUpdated);
              return true;
            } catch (retryError) {
              const retryMessage = extractApiErrorMessage(retryError?.response)
                || retryError?.response?.data?.message
                || this.text.exports.modal.toast.formatError;
              this.$toast?.error(retryMessage);
              return false;
            }
          }

          return false;
        }

        this.$toast?.error(backendMessage || this.text.exports.modal.toast.formatError);
        return false;
      } finally {
        this.savingFormat = false;
      }
    },
    async handleDeleteFormat() {
      if (!this.selectedFormat || !this.canDeleteFormat) {
        this.closeDeleteConfirm();
        return;
      }

      this.deletingFormat = true;

      try {
        const exportFormatDelete = this.getServiceMethod('exportFormatDelete');
        await exportFormatDelete(this.selectedFormat.id);
        await this.reloadFormats(0);
        this.selectedFormatId = 'new';
        this.formatName = '';
        this.applyDefaultColumns();
        this.$toast?.success(this.text.exports.modal.toast.formatDeleted);
      } catch (error) {
        const message = error?.response?.data?.message || this.text.exports.modal.toast.formatDeleteError;
        this.$toast?.error(message);
      } finally {
        this.deletingFormat = false;
        this.closeDeleteConfirm();
      }
    },
    async reloadFormats(selectedId = 0) {
      const exportFormats = this.getServiceMethod('exportFormats');
      const response = await exportFormats();
      const payload = extractCollection(response);
      this.formats = Array.isArray(payload) ? payload : [];

      if (Number(selectedId || 0) > 0) {
        this.selectedFormatId = String(selectedId);
      }

      this.onFormatChange();
    },
    buildExportPayload() {
      return {
        ...(this.extraPayload || {}),
        scope: 'all',
        filters: this.filters,
        search: String(this.searchTerm || '').trim(),
        page_customer_ids: Array.isArray(this.pageCustomerIds) ? this.pageCustomerIds : [],
        selected_columns: this.selectedColumns,
        format_name: String((this.selectedFormat?.name || this.formatName || '')).trim(),
        output_type: this.exportOutput,
        include_summary: this.includeSummary ? 1 : 0,
        include_logo: this.includeLogo ? 1 : 0,
      };
    },
    async handleSaveAndExport() {
      if (!this.allowExport) {
        return;
      }

      const saveSucceeded = await this.handleSaveFormat();
      if (!saveSucceeded || this.formatNameError !== '' || !this.canExport) {
        return;
      }

      await this.handleExport();
    },
    async handleExport() {
      if (!this.allowExport) {
        return;
      }

      if (!this.canExport) {
        this.$toast?.error(this.text.exports.modal.toast.noColumns);
        return;
      }

      this.exporting = true;

      try {
        const output = this.supportsPdfExport
          ? String(this.exportOutput || 'excel').trim().toLowerCase()
          : 'excel';
        const methodName = output === 'pdf' ? 'exportPdf' : 'exportExcel';
        const exportMethod = this.getServiceMethod(methodName);

        const blob = await exportMethod(this.buildExportPayload());
        if (!(blob instanceof Blob)) {
          throw new Error(this.text.exports.modal.toast.exportError);
        }

        const downloadUrl = window.URL.createObjectURL(blob);
        const extension = output === 'pdf' ? 'pdf' : 'xlsx';
        const fileName = `${String(this.fileNamePrefix || 'export').trim() || 'export'}_${new Date().toISOString().slice(0, 10)}.${extension}`;

        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        this.$toast?.success(this.text.exports.modal.toast.exportSuccess);
      } catch (error) {
        const message = extractApiErrorMessage(error?.response)
          || error?.response?.data?.message
          || this.text.exports.modal.toast.exportError;
        this.$toast?.error(message);
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
.customer-export-modal__option-checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.customer-export-modal__option-checkboxes .app-modal-toggle {
  display: inline-flex;
  align-items: center;
  column-gap: 12px;
  width: auto;
  min-width: 148px;
  flex: 0 0 auto;
  padding-left: 12px;
  padding-right: 12px;
}

.customer-export-modal__option-checkboxes .app-modal-toggle input {
  flex: 0 0 17px;
  margin: 0;
  vertical-align: middle;
}

.customer-export-modal__option-checkboxes .app-modal-toggle span {
  display: inline-block;
  line-height: 1.25;
  white-space: nowrap;
  margin: 0;
}

.customer-export-modal__summary-toggle {
  margin-top: 10px;
  width: auto;
  min-width: 240px;
  max-width: 100%;
}

@media (max-width: 900px) {
  .customer-export-modal__option-checkboxes .app-modal-toggle {
    min-width: 132px;
  }
}
</style>
